import React, { useState, useRef, useCallback } from 'react';
import { fetchLessonSource, analyzeLessonImage, formatDuration } from '../../utils/lessonAiSourceClient';
import { fetchLessonOutline, modifyLessonOutline, modifyLessonSection } from '../../utils/lessonAiOutlineClient';
import { getTipTapBlocksForSectionWithFallback } from '../../utils/lessonAiContentBlocks';
import { scheduleInterleavedOutlineInsert } from '../../utils/applySmartLessonOutline';
import OutlinePreview from './OutlinePreview';

// ─── Constants ────────────────────────────────────────────────────────────────

const CEFR      = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const DURATIONS = [30, 45, 60, 90];

const SKILLS = [
  { id: 'reading',    label: 'Reading',    icon: '📖' },
  { id: 'listening',  label: 'Listening',  icon: '👂' },
  { id: 'speaking',   label: 'Speaking',   icon: '💬' },
  { id: 'grammar',    label: 'Grammar',    icon: '✍️' },
  { id: 'vocabulary', label: 'Vocabulary', icon: '📚' },
  { id: 'writing',    label: 'Writing',    icon: '🖊️' },
];

const LESSON_TYPES = [
  { id: 'text_based',   label: 'Text-based',   icon: '📖', desc: 'Article + comprehension' },
  { id: 'conversation', label: 'Conversation', icon: '🗣️', desc: 'Speaking & dialogue' },
  { id: 'vocabulary',   label: 'Vocabulary',   icon: '📚', desc: 'Words & collocations' },
  { id: 'grammar',      label: 'Grammar',      icon: '✍️', desc: 'Form & meaning' },
  { id: 'listening',    label: 'Listening',    icon: '🎧', desc: 'Audio & transcript' },
];

// ─── Tokens ───────────────────────────────────────────────────────────────────

const clr = {
  purple:      '#7c3aed',
  purpleSoft:  '#ede9fe',
  purpleText:  '#5b21b6',
  purpleBorder:'#c4b5fd',
  gray50:      '#f9fafb',
  gray100:     '#f3f4f6',
  gray200:     '#e5e7eb',
  gray400:     '#9ca3af',
  gray600:     '#4b5563',
  gray900:     '#111827',
  red50:       '#fef2f2',
  redText:     '#b91c1c',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isUrl(s) {
  try { new URL(s); return s.startsWith('http'); } catch { return false; }
}

function domainLabel(url) {
  try { return new URL(url).hostname.replace('www.', ''); } catch { return url; }
}

/** Ensure every section has contentBlocks (compute fallback upfront for preview) */
function hydrateSections(sections, cefrLevel, lessonTitle) {
  return (sections || []).map(sec => {
    if (Array.isArray(sec.contentBlocks) && sec.contentBlocks.length > 0) return sec;
    const blocks = getTipTapBlocksForSectionWithFallback(sec, cefrLevel, { lessonTitle });
    return { ...sec, contentBlocks: blocks };
  });
}

// ─── Icon components ──────────────────────────────────────────────────────────

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ display: 'block' }}>
      <path d="M13 16l-5-5 5-5" stroke={clr.purple} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ display: 'block' }}>
      <path d="M1 1l12 12M13 1L1 13" stroke={clr.gray400} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function SpinnerIcon({ color }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'slSpin 0.8s linear infinite', display: 'block' }}>
      <circle cx="8" cy="8" r="6" stroke={clr.gray200} strokeWidth="2.5" />
      <path d="M14 8a6 6 0 00-6-6" stroke={color || clr.purple} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Pill({ active, onClick, children, wide }) {
  return (
    <button
      onClick={onClick}
      type="button"
      style={{
        flex: wide ? 1 : undefined,
        padding: '5px 10px',
        border: active ? `1.5px solid ${clr.purple}` : `1.5px solid transparent`,
        borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        background: active ? clr.purpleSoft : clr.gray100,
        color: active ? clr.purpleText : clr.gray600,
        transition: 'all 100ms ease',
      }}
    >
      {children}
    </button>
  );
}

function SourceCard({ source, onClear }) {
  const isYT = source.type === 'youtube';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 10px 10px 12px', borderRadius: 10, background: '#fff', border: `1.5px solid ${clr.gray200}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ flexShrink: 0, width: 52, height: 36, borderRadius: 6, overflow: 'hidden', background: clr.gray100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isYT && source.thumbnailUrl
          ? <img src={source.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : source.imageUrl
            ? <img src={source.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 18 }}>{isYT ? '▶️' : '📰'}</span>
        }
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: clr.gray900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {source.title || (isYT ? 'YouTube Video' : 'Article')}
        </div>
        <div style={{ fontSize: 11, color: clr.gray400, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ background: isYT ? '#fee2e2' : '#dcfce7', color: isYT ? '#dc2626' : '#16a34a', borderRadius: 4, padding: '1px 5px', fontWeight: 600, fontSize: 10 }}>
            {isYT ? 'YouTube' : (source.sourceName || domainLabel(source.url || ''))}
          </span>
          {isYT && source.durationSeconds ? <span>{formatDuration(source.durationSeconds)}</span> : null}
          {isYT ? (
            <span style={{ color: source.hasTranscript ? '#16a34a' : clr.gray400 }}>
              {source.hasTranscript ? '✓ transcript' : 'no transcript'}
            </span>
          ) : source.wordCount ? <span>~{source.wordCount} words</span> : null}
        </div>
        {source.description && (
          <div style={{ fontSize: 11, color: clr.gray600, marginTop: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {source.description}
          </div>
        )}
      </div>
      <button onClick={onClear} title="Remove source" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: clr.gray400, display: 'flex', alignItems: 'center', flexShrink: 0, borderRadius: 4, marginTop: -2 }}
        onMouseEnter={e => e.currentTarget.style.color = clr.gray900}
        onMouseLeave={e => e.currentTarget.style.color = clr.gray400}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

function ImagePreviewCard({ imageAnalysis, onClear }) {
  const typeLabels = { text: 'Text extracted', scene: 'Visual scene', whiteboard: 'Handwritten notes', mixed: 'Image + text' };
  const typeIcons  = { text: '📄', scene: '🖼️', whiteboard: '🖊️', mixed: '📋' };
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 10px 10px 12px', borderRadius: 10, background: '#fff', border: `1.5px solid ${clr.purpleBorder}`, boxShadow: '0 1px 4px rgba(124,58,237,0.08)' }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: clr.purpleSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
        {typeIcons[imageAnalysis.detectedType] || '🖼️'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: clr.gray900 }}>{imageAnalysis.suggestedTitle || 'Image lesson'}</div>
        <div style={{ fontSize: 11, marginTop: 2, display: 'flex', gap: 4, alignItems: 'center' }}>
          <span style={{ background: clr.purpleSoft, color: clr.purpleText, borderRadius: 4, padding: '1px 5px', fontWeight: 600, fontSize: 10 }}>
            {typeLabels[imageAnalysis.detectedType] || 'Image'}
          </span>
          {imageAnalysis.suggestedLevel && <span style={{ color: clr.gray400 }}>· {imageAnalysis.suggestedLevel}</span>}
        </div>
        {imageAnalysis.contentSummary && (
          <div style={{ fontSize: 11, color: clr.gray600, marginTop: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {imageAnalysis.contentSummary}
          </div>
        )}
      </div>
      <button onClick={onClear} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: clr.gray400, display: 'flex', alignItems: 'center', flexShrink: 0, borderRadius: 4, marginTop: -2 }}
        onMouseEnter={e => e.currentTarget.style.color = clr.gray900}
        onMouseLeave={e => e.currentTarget.style.color = clr.gray400}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

function TopicCard({ topic, onClear }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px 10px 12px', borderRadius: 10, background: '#fff', border: `1.5px solid ${clr.gray200}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>💡</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: clr.gray400, marginBottom: 1 }}>TOPIC</div>
        <div style={{ fontSize: 13, color: clr.gray900, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>"{topic}"</div>
      </div>
      <button onClick={onClear} title="Change" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: clr.gray400, display: 'flex', alignItems: 'center', flexShrink: 0, borderRadius: 4 }}
        onMouseEnter={e => e.currentTarget.style.color = clr.gray900}
        onMouseLeave={e => e.currentTarget.style.color = clr.gray400}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SmartLessonPanel({ editor, onInsertToBoard, onInsertSmartLesson, onBack, onClose }) {
  // Source
  const [inputValue, setInputValue]       = useState('');
  const [sourceFetching, setSourceFetching] = useState(false);
  const [sourceError, setSourceError]     = useState('');
  const [source, setSource]               = useState(null);
  const [imageAnalysis, setImageAnalysis] = useState(null);
  const [imageFile, setImageFile]         = useState(null);
  const [sourceMode, setSourceMode]       = useState(null);
  const [inputFocused, setInputFocused]   = useState(false);
  const [fetchElapsed, setFetchElapsed]   = useState(0);
  const fetchTimerRef  = useRef(null);
  const fetchAbortRef  = useRef(null);
  const fileInputRef   = useRef(null);
  const [dragOver, setDragOver]           = useState(false);

  // Settings
  const [cefrLevel, setCefrLevel]         = useState('B1');
  const [duration, setDuration]           = useState(60);
  const [skills, setSkills]               = useState(['reading', 'vocabulary', 'speaking']);
  const [lessonType, setLessonType]       = useState('text_based');
  const [textMode, setTextMode]           = useState('adapted'); // 'adapted' | 'original'

  // Generation
  const [generating, setGenerating]       = useState(false);
  const [genError, setGenError]           = useState('');
  const [outline, setOutline]             = useState(null);
  const [localSections, setLocalSections] = useState([]);

  // Modification
  const [modifyingAll, setModifyingAll]           = useState(false);
  const [modifyingSectionIdx, setModifyingSectionIdx] = useState(null);

  // ── Source handling ──────────────────────────────────────────────────────────

  const handleInputChange = (val) => {
    setInputValue(val);
    setSourceError('');
    if (isUrl(val.trim())) setSourceMode('url');
    else if (val.trim()) setSourceMode('topic');
    else setSourceMode(null);
  };

  const handleFetchUrl = useCallback(async (url) => {
    setSourceFetching(true);
    setSourceError('');
    setFetchElapsed(0);
    fetchTimerRef.current = setInterval(() => setFetchElapsed(s => s + 1), 1000);
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const clientTimeout = setTimeout(() => controller.abort(), 15_000);
    try {
      const result = await fetchLessonSource(url, controller.signal);
      setSource(result);
      setSourceMode('url');
      setInputValue('');
      if (result.type === 'youtube' && result.hasTranscript) {
        setSkills(prev => prev.includes('listening') ? prev : ['listening', ...prev].slice(0, 3));
        setLessonType('listening');
      }
    } catch (e) {
      const msg = e.name === 'AbortError'
        ? 'Request timed out. Try a different URL or paste the article text directly.'
        : e.message || 'Could not fetch this URL.';
      setSourceError(msg);
    } finally {
      clearInterval(fetchTimerRef.current);
      clearTimeout(clientTimeout);
      setSourceFetching(false);
      setFetchElapsed(0);
    }
  }, []);

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && isUrl(inputValue.trim())) {
      e.preventDefault();
      handleFetchUrl(inputValue.trim());
    }
  };

  const handleImageFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) { setSourceError('Please upload an image file (JPG, PNG, WEBP).'); return; }
    if (file.size > 10 * 1024 * 1024) { setSourceError('Image too large. Max 10 MB.'); return; }
    setSourceFetching(true);
    setSourceError('');
    setSourceMode('image');
    setImageFile(file);
    setFetchElapsed(0);
    fetchTimerRef.current = setInterval(() => setFetchElapsed(s => s + 1), 1000);
    try {
      const result = await analyzeLessonImage(file);
      setImageAnalysis(result);
      if (result.suggestedLevel) setCefrLevel(result.suggestedLevel);
    } catch (e) {
      setSourceError(e.message || 'Could not analyze image.');
      setImageFile(null);
      setSourceMode(null);
    } finally {
      clearInterval(fetchTimerRef.current);
      setSourceFetching(false);
      setFetchElapsed(0);
    }
  }, []);

  const clearSource = () => {
    setSource(null); setImageAnalysis(null); setImageFile(null);
    setSourceMode(null); setInputValue(''); setSourceError('');
    setOutline(null); setLocalSections([]);
  };

  const toggleSkill = (id) =>
    setSkills(prev => prev.includes(id)
      ? prev.length > 1 ? prev.filter(s => s !== id) : prev
      : [...prev, id]);

  const hasSourceOrTopic = source || imageAnalysis || (sourceMode === 'topic' && inputValue.trim());
  const looksLikeUrl = isUrl(inputValue.trim());

  // ── Generate ─────────────────────────────────────────────────────────────────

  const buildPayload = () => {
    let teacherMaterial = '';
    let title = '';
    if (source) {
      title = source.title || (source.type === 'youtube' ? 'Video lesson' : 'Reading lesson');
      teacherMaterial = source.type === 'youtube' && !source.hasTranscript
        ? `YouTube video: "${source.title}" by ${source.channel || 'unknown'}. URL: ${source.url}`
        : source.text || '';
    } else if (imageAnalysis) {
      title = imageAnalysis.suggestedTitle || 'Image-based lesson';
      teacherMaterial = imageAnalysis.extractedText || imageAnalysis.sceneDescription || '';
    } else {
      title = inputValue.trim() || 'Smart lesson';
    }
    return {
      title,
      teacherMaterial: teacherMaterial.slice(0, 15_000),
      filters: {
        cefrLevel, durationMinutes: duration, skillSections: skills,
        exerciseTypes: [], lessonType,
        // Only relevant when an article URL is the source. For topic/image
        // flows there's nothing to "keep original", so we default to adapted.
        textMode: source?.type === 'article' ? textMode : 'adapted',
        allowYoutube: true, allowExternalLinks: true, authenticSourcesOnly: false,
      },
    };
  };

  const handleGenerate = async () => {
    if (!hasSourceOrTopic) { setGenError('Add a source or describe a topic first.'); return; }
    setGenerating(true);
    setGenError('');
    setOutline(null);
    setLocalSections([]);
    try {
      const data = await fetchLessonOutline(buildPayload());
      const hydrated = hydrateSections(data.sections, data.cefrLevel, data.lessonTitle);
      setOutline(data);
      setLocalSections(hydrated);
    } catch (e) {
      setGenError(e.message || 'Generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // ── Modification ──────────────────────────────────────────────────────────────

  const handleModifyAll = async (instruction) => {
    if (!outline) return;
    setModifyingAll(true);
    try {
      const result = await modifyLessonOutline({
        lessonTitle: outline.lessonTitle,
        cefrLevel: outline.cefrLevel,
        sections: localSections,
        instruction,
      });
      if (Array.isArray(result.sections)) {
        setLocalSections(hydrateSections(result.sections, outline.cefrLevel, outline.lessonTitle));
      }
    } catch (e) {
      setGenError(e.message || 'Modification failed.');
    } finally {
      setModifyingAll(false);
    }
  };

  const handleModifySection = async (idx, instruction) => {
    if (!outline) return;
    setModifyingSectionIdx(idx);
    try {
      const result = await modifyLessonSection({
        lessonTitle: outline.lessonTitle,
        cefrLevel: outline.cefrLevel,
        section: localSections[idx],
        instruction,
      });
      if (result.section) {
        const hydrated = hydrateSections([result.section], outline.cefrLevel, outline.lessonTitle);
        setLocalSections(prev => prev.map((s, i) => i === idx ? hydrated[0] : s));
      }
    } catch (e) {
      setGenError(e.message || 'Section modification failed.');
    } finally {
      setModifyingSectionIdx(null);
    }
  };

  // ── Insert ────────────────────────────────────────────────────────────────────

  const handleInsert = () => {
    if (!outline) return;
    const finalOutline = { ...outline, sections: localSections };
    const applyMeta = {
      teacherMaterial: source?.text || imageAnalysis?.extractedText || '',
      mode: (source?.text || imageAnalysis?.extractedText) ? 'with_material' : 'clean',
      youtubeUrl: source?.type === 'youtube' ? (source.url || '') : null,
    };
    if (onInsertSmartLesson) {
      onInsertSmartLesson(finalOutline, applyMeta);
    } else if (editor && typeof editor.chain === 'function') {
      scheduleInterleavedOutlineInsert(finalOutline, () => editor, [Date.now()], applyMeta);
    } else if (onInsertToBoard) {
      onInsertToBoard(finalOutline);
    }
    onClose?.();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes slSpin    { to { transform: rotate(360deg); } }
        @keyframes slFadeIn  { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
        .sl-input::placeholder { color: #9ca3af; }
        .sl-input:focus { border-color: #7c3aed !important; box-shadow: 0 0 0 3px rgba(124,58,237,0.1) !important; }
        .sl-gen-btn:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); box-shadow: 0 6px 20px rgba(124,58,237,0.35) !important; }
        .sl-gen-btn:active:not(:disabled) { transform: translateY(0); }
        .sl-icon-btn:hover { background: ${clr.gray100} !important; }
        .sl-type-btn:hover { border-color: #7c3aed !important; }
      `}</style>

      <div onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <button onClick={onBack} className="sl-icon-btn" type="button"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 100ms' }}>
            <BackIcon />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <div style={{ width: 26, height: 26, borderRadius: 7, background: 'linear-gradient(135deg, #7c3aed, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>✨</div>
            <span style={{ fontSize: 14, fontWeight: 700, color: clr.gray900, letterSpacing: '-0.01em' }}>Smart Lesson</span>
            <span style={{ fontSize: 11, background: clr.purpleSoft, color: clr.purpleText, borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>AI</span>
          </div>
          <button onClick={onClose} className="sl-icon-btn" type="button" title="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 100ms' }}>
            <CloseIcon />
          </button>
        </div>

        {/* Source input */}
        {!source && !imageAnalysis && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, animation: 'slFadeIn 0.2s ease' }}>
            <div style={{ position: 'relative' }}>
              <input
                value={inputValue}
                onChange={e => handleInputChange(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                placeholder="Paste YouTube / article URL, or describe a topic…"
                className="sl-input"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: looksLikeUrl ? '9px 68px 9px 12px' : '9px 12px',
                  borderRadius: 9, fontSize: 13,
                  border: `1.5px solid ${inputFocused ? clr.purple : clr.gray200}`,
                  background: clr.gray50, outline: 'none', color: clr.gray900,
                  transition: 'border-color 150ms, box-shadow 150ms',
                }}
              />
              {looksLikeUrl && !sourceFetching && (
                <button onClick={() => handleFetchUrl(inputValue.trim())} type="button"
                  style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', background: clr.purple, border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '4px 10px', fontSize: 11, fontWeight: 700 }}>
                  Fetch →
                </button>
              )}
            </div>

            {sourceFetching && sourceMode === 'url' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 9, background: clr.purpleSoft, border: `1.5px solid ${clr.purpleBorder}` }}>
                <SpinnerIcon />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: clr.purpleText }}>
                    {fetchElapsed < 4 ? 'Fetching source…' : fetchElapsed < 9 ? 'Still loading…' : 'Taking a bit longer…'}
                  </div>
                  <div style={{ fontSize: 11, color: clr.purple, marginTop: 1 }}>{fetchElapsed}s</div>
                </div>
                <button onClick={() => fetchAbortRef.current?.abort()} type="button"
                  style={{ fontSize: 11, color: clr.purple, background: 'rgba(124,58,237,0.1)', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '2px 6px', borderRadius: 5 }}>
                  Cancel
                </button>
              </div>
            )}

            {sourceFetching && sourceMode === 'image' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 9, background: clr.purpleSoft, border: `1.5px solid ${clr.purpleBorder}` }}>
                <SpinnerIcon />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: clr.purpleText }}>
                    {fetchElapsed < 4 ? 'Analysing image…'
                      : fetchElapsed < 9 ? 'Reading text & describing the scene…'
                      : 'Almost there…'}
                  </div>
                  <div style={{ fontSize: 11, color: clr.purple, marginTop: 1 }}>
                    {imageFile?.name && <span style={{ marginRight: 6 }}>{imageFile.name}</span>}
                    <span>{fetchElapsed}s</span>
                  </div>
                </div>
              </div>
            )}

            {!sourceFetching && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 1, background: clr.gray200 }} />
                <span style={{ fontSize: 11, color: clr.gray400, fontWeight: 500 }}>or upload image</span>
                <div style={{ flex: 1, height: 1, background: clr.gray200 }} />
              </div>
            )}

            {!sourceFetching && (
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer?.files?.[0]; if (f) handleImageFile(f); }}
                onClick={() => fileInputRef.current?.click()}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 9, cursor: 'pointer', border: `2px dashed ${dragOver ? clr.purple : clr.gray200}`, background: dragOver ? clr.purpleSoft : clr.gray50, transition: 'border-color 150ms, background 150ms' }}
              >
                <div style={{ fontSize: 22, flexShrink: 0, lineHeight: 1 }}>{dragOver ? '📸' : '🖼️'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: clr.gray900 }}>Drop image here</div>
                  <div style={{ fontSize: 11, color: clr.gray400 }}>Photo · Whiteboard · Screenshot</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: clr.purple, background: clr.purpleSoft, borderRadius: 6, padding: '4px 10px', flexShrink: 0 }}>Browse</span>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleImageFile(f); }} />
              </div>
            )}
          </div>
        )}

        {/* Source / image cards */}
        {(source || imageAnalysis) && (
          <div style={{ animation: 'slFadeIn 0.2s ease' }}>
            {source       && <SourceCard source={source} onClear={clearSource} />}
            {imageAnalysis && <ImagePreviewCard imageAnalysis={imageAnalysis} onClear={clearSource} />}
          </div>
        )}

        {/* Article text mode picker — only for article URLs. Lets the
            teacher choose between using the original article verbatim
            or having AI adapt the text to the student's CEFR level. */}
        {source?.type === 'article' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, animation: 'slFadeIn 0.2s ease' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: clr.gray500, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Reading text
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { id: 'adapted', label: `Adapt to ${cefrLevel}`, sub: 'Rewrite simpler for the student\'s level' },
                { id: 'original', label: 'Use original', sub: 'Keep article verbatim, paragraph by paragraph' },
              ].map(opt => {
                const active = textMode === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTextMode(opt.id)}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: 9,
                      border: active ? `1.5px solid ${clr.purple}` : `1px solid ${clr.gray200}`,
                      background: active ? clr.purpleSoft : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 140ms ease',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: active ? clr.purpleText : clr.gray900, marginBottom: 2 }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: 10.5, color: active ? clr.purple : clr.gray500, lineHeight: 1.35 }}>
                      {opt.sub}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Topic card */}
        {!source && !imageAnalysis && sourceMode === 'topic' && inputValue.trim() && (
          <TopicCard topic={inputValue.trim()} onClear={() => { setInputValue(''); setSourceMode(null); }} />
        )}

        {/* Error banner */}
        {sourceError && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: clr.red50, color: clr.redText, fontSize: 12, fontWeight: 500 }}>
            {sourceError}
          </div>
        )}

        {/* Settings */}
        {hasSourceOrTopic && !outline && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4, animation: 'slFadeIn 0.2s ease' }}>
            <div style={{ height: 1, background: clr.gray200 }} />

            {/* Lesson type */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: clr.gray400, letterSpacing: '0.06em', marginBottom: 6, textTransform: 'uppercase' }}>Lesson type</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {LESSON_TYPES.map(lt => (
                  <button
                    key={lt.id}
                    onClick={() => setLessonType(lt.id)}
                    className="sl-type-btn"
                    type="button"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 10px', borderRadius: 8, cursor: 'pointer',
                      border: lessonType === lt.id ? `1.5px solid ${clr.purple}` : `1.5px solid ${clr.gray200}`,
                      background: lessonType === lt.id ? clr.purpleSoft : '#fff',
                      transition: 'all 120ms',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: 15, flexShrink: 0 }}>{lt.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: lessonType === lt.id ? clr.purpleText : clr.gray900 }}>{lt.label}</div>
                      <div style={{ fontSize: 11, color: clr.gray400 }}>{lt.desc}</div>
                    </div>
                    {lessonType === lt.id && (
                      <span style={{ fontSize: 14, color: clr.purple, flexShrink: 0 }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* CEFR + Duration */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: clr.gray400, letterSpacing: '0.06em', marginBottom: 5, textTransform: 'uppercase' }}>Level</div>
                <div style={{ display: 'flex', gap: 3 }}>
                  {CEFR.map(l => <Pill key={l} active={cefrLevel === l} onClick={() => setCefrLevel(l)} wide>{l}</Pill>)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: clr.gray400, letterSpacing: '0.06em', marginBottom: 5, textTransform: 'uppercase' }}>Duration</div>
                <div style={{ display: 'flex', gap: 3 }}>
                  {DURATIONS.map(d => <Pill key={d} active={duration === d} onClick={() => setDuration(d)} wide>{d}m</Pill>)}
                </div>
              </div>
            </div>

            {/* Skills */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: clr.gray400, letterSpacing: '0.06em', marginBottom: 5, textTransform: 'uppercase' }}>Focus skills</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {SKILLS.map(s => (
                  <Pill key={s.id} active={skills.includes(s.id)} onClick={() => toggleSkill(s.id)}>
                    {s.icon} {s.label}
                  </Pill>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Outline preview */}
        {outline && localSections.length > 0 && (
          <div style={{ animation: 'slFadeIn 0.25s ease' }}>
            <div style={{ height: 1, background: clr.gray200, marginBottom: 10 }} />
            <OutlinePreview
              outline={outline}
              sections={localSections}
              onSectionsChange={setLocalSections}
              onInsert={handleInsert}
              onRegenerate={() => { setOutline(null); setLocalSections([]); handleGenerate(); }}
              onModifySection={handleModifySection}
              onModifyAll={handleModifyAll}
              modifyingAll={modifyingAll}
              modifyingSectionIdx={modifyingSectionIdx}
              generating={generating}
            />
          </div>
        )}

        {/* Generate button */}
        {!outline && hasSourceOrTopic && (
          <button
            onClick={handleGenerate}
            disabled={generating}
            type="button"
            className="sl-gen-btn"
            style={{
              width: '100%', padding: '11px 14px', borderRadius: 9, border: 'none',
              background: generating ? clr.purpleSoft : 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              color: generating ? clr.purpleText : '#fff',
              cursor: generating ? 'wait' : 'pointer',
              fontWeight: 700, fontSize: 13, letterSpacing: '0.01em',
              boxShadow: generating ? 'none' : '0 4px 14px rgba(124,58,237,0.28)',
              transition: 'all 200ms cubic-bezier(0.4,0,0.2,1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {generating ? <><SpinnerIcon /><span>Generating lesson…</span></> : '✨ Generate Smart Lesson'}
          </button>
        )}

        {/* Generation error */}
        {genError && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: clr.red50, color: clr.redText, fontSize: 12, fontWeight: 500 }}>
            {genError}
          </div>
        )}
      </div>
    </>
  );
}
